import { memo } from "react";
import { Alert, Box, Grid2, Skeleton, Typography } from "@mui/material";
import ArrowButton from "@/ui-kit/ArrowButton";
import { useNavigate } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";
import { useChatSelectionSlice } from "@store/slices/chatSelection";
import ChatDetailsHeader from "./ChatDetailsHeader";
import { useMutation, useQuery } from "@apollo/client";
import { ContentNode, Mutation, MutationRenameChatArgs, MutationCreateChatArgs, Query } from "@/app/graphql/codegen";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { RENAME_CHAT } from "@chat/graphql/mutations/renameChat";
import { FETCH_CONTEXT_SELECTION_FOR_CHAT } from "@chat/graphql/queries/fetchContextSelectionForChat";
import { useChatMessages } from "@chat/hooks/useChatMessages";
import ChatMessage from "@chat/components/main/ChatMessage";
import { useDebugBorder } from "@core/hooks/useDebugBorder";
import { CREATE_CHAT } from "@chat/graphql/mutations/createChat";
import { icon } from "@/app/style/icons";
import { useState } from "react";
import DocChip from "@chat/components/DocChip";
import InfoRow from "./InfoRow";

/**
 * TODO @lwun refactor
 */
const ChatDetails = memo(() => {
    const navigate = useNavigate();
    const { id } = useChatSelectionSlice();
    const chatBorder = useDebugBorder("orange");
    const [isCreatingChat, setIsCreatingChat] = useState(false);

    const {
        data,
        loading: isChatsLoading,
        error: chatsError,
        refetch: refetchChats,
    } = useQuery<Pick<Query, "chats">>(FETCH_CHATS, { errorPolicy: "all" });
    const chats = data?.chats ?? [];

    const { messages } = useChatMessages(id, 2);

    const [renameChatMutation] = useMutation<Pick<Mutation, "renameChat">, MutationRenameChatArgs>(RENAME_CHAT);
    const [createChatMutation] = useMutation<Pick<Mutation, "createChat">, MutationCreateChatArgs>(CREATE_CHAT);

    const renameChat = async (chatId: string, newName: string) => {
        if (!chatId) return;
        const result = await renameChatMutation({
            variables: { id: chatId, name: newName },
            refetchQueries: [{ query: FETCH_CHATS }],
        });
        return result.data?.renameChat;
    };

    const handleCreateChat = async () => {
        setIsCreatingChat(true);
        try {
            const result = await createChatMutation({
                variables: { name: "Neuer Chat", isPrivate: true },
                refetchQueries: [{ query: FETCH_CHATS }],
            });

            if (result.data?.createChat) {
                navigate(`${RouteTo.CHAT}/${result.data.createChat.id}`);
            }
        } catch (error) {
            console.error("Fehler beim Erstellen des Chats:", error);
        } finally {
            setIsCreatingChat(false);
        }
    };

    const { data: selectionData, loading: isLoading } = useQuery<Pick<Query, "contextForChat">>(FETCH_CONTEXT_SELECTION_FOR_CHAT, {
        variables: { chatId: id },
        skip: !id,
    });

    const selectedContentNodes = selectionData?.contextForChat?.contentNodes?.filter((n): n is ContentNode => n !== null) || [];

    const skeletonHeight = 32;

    // Find the selected chat in our real-time data
    const chat = chats.find((c) => c.id === id);

    // Loading state (avoid showing empty state while data is still fetching)
    if (isChatsLoading && chats.length === 0) {
        return (
            <Grid2 container size={12} spacing={2}>
                <Grid2 container size={12} spacing={1}>
                    <Grid2 size={"grow"}>
                        <Skeleton width={200} height={skeletonHeight} />
                    </Grid2>

                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                </Grid2>

                <Grid2 size={12}>
                    <Skeleton height={240} />
                </Grid2>

                <Grid2 container size={12}>
                    <Grid2>
                        <Skeleton width={82} height={skeletonHeight} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={96} height={skeletonHeight} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={69} height={skeletonHeight} />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    }

    // Error state (only when we have no cached data to show)
    if (chatsError && chats.length === 0) {
        return (
            <Grid2 container size={12} spacing={2}>
                <Grid2 size={12}>
                    <Alert
                        severity="error"
                        action={
                            <ArrowButton onClick={() => refetchChats()} sx={{ px: 2 }}>
                                Erneut versuchen
                            </ArrowButton>
                        }
                    >
                        Fehler beim Laden der Chats: {chatsError.message}
                    </Alert>
                </Grid2>
            </Grid2>
        );
    }

    // Render empty state when no chats exist
    if (chats.length === 0) {
        return (
            <Grid2 container size={12} spacing={6} justifyContent={"center"} alignItems={"center"} height={"100%"}>
                <Grid2 container size={12} spacing={2} textAlign={"center"}>
                    <Grid2 size={12}>
                        <Typography variant={"h4"}>Willkommen!</Typography>
                    </Grid2>

                    <Grid2 size={12}>
                        <Typography variant={"body1"}>Erstellen Sie Ihren ersten Chat, um loszulegen</Typography>
                    </Grid2>

                    <Grid2 size={12} pt={4}>
                        <ArrowButton sx={{ px: 4 }} onClick={handleCreateChat} disabled={isCreatingChat} startIcon={<icon.add sx={{ color: "semantic.icon.functional" }} />}>
                            Ersten Chat erstellen
                        </ArrowButton>
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    }

    return chat ? (
        <Grid2 container size={12} spacing={6} justifyContent={"end"}>
            <ChatDetailsHeader id={id || ""} name={chat.name} isPrivate={!!chat.isPrivate} />

            {/* Chat statistics */}
            <Grid2 container size={12} spacing={{ xs: 1, sm: 1, md: 1, lg: 12 }}>
                <Grid2 container size={{ xs: 12, sm: 12, md: 12, lg: 6 }} spacing={1}>
                    <InfoRow title={"Befragte Seiten"} content={chat.totalPagesSpent.toString()} />
                    <InfoRow title={"Nachrichten"} content={chat.sizeInMessages?.toString()} />
                    <InfoRow
                        title={"Zuletzt bearbeitet"}
                        content={
                            chat.createdAt &&
                            new Date(parseInt(chat.updatedAt)).toLocaleString("de-DE", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                            })
                        }
                    />
                </Grid2>

                <Grid2 container size={{ xs: 12, sm: 12, md: 12, lg: 6 }} spacing={1}>
                    <InfoRow
                        title={"Erstellt am"}
                        content={
                            chat.createdAt &&
                            new Date(parseInt(chat.createdAt)).toLocaleString("de-DE", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                            })
                        }
                    />
                    <InfoRow title={"Erstellt von"} content={chat.creatorName || "Unknown"} />

                    {/* FIXME Notwendig, da sich sonst das Layout verschiebt */}
                    <Box sx={{ opacity: 0 }}>
                        <InfoRow title={""} content={""} />
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container size={12} spacing={2}>
                <Grid2 size={12}>
                    <Typography variant={"h5"}>Befragte Dokumente</Typography>
                </Grid2>

                <Grid2 container size={"grow"} spacing={1}>
                    {selectedContentNodes.length > 0 ? (
                        selectedContentNodes.map((node) => (
                            <Grid2 key={node.id}>
                                <DocChip node={node} flagStyle={{ marginRight: -4 }} showTooltip={true} />
                            </Grid2>
                        ))
                    ) : (
                        <Grid2>
                            <Typography>Keine Dokumente zugewiesen</Typography>
                        </Grid2>
                    )}
                </Grid2>
            </Grid2>

            <Grid2 size={12}>
                <ArrowButton fullWidth onClick={() => navigate(`${RouteTo.CHAT}/${id}`)}>
                    Chat öffnen
                </ArrowButton>
            </Grid2>

            <Grid2 container size={12} spacing={2}>
                <Grid2 size={12}>
                    <Typography variant={"h5"}>Letzte Frage</Typography>
                </Grid2>

                <Grid2
                    container
                    size={12}
                    border={chatBorder}
                    overflow={"hidden"}
                    sx={{
                        "&::-webkit-scrollbar": { display: "none" }, // Für Webkit
                        scrollbarWidth: "none", // Für Firefox
                    }}
                >
                    {messages.length > 0 ? (
                        messages.map((message) => (
                            <Grid2 key={message.id} size={12} /* ... other props ... */>
                                <ChatMessage 
                                    message={message} 
                                    hideCopy 
                                    hideDelete 
                                />
                            </Grid2>
                        ))
                    ) : (
                        <Grid2>
                            <Typography>Bisher keine Nachrichten</Typography>
                        </Grid2>
                    )}
                </Grid2>
            </Grid2>
        </Grid2>
    ) : (
        <Grid2 container size={12} spacing={2}>
            <Grid2 container size={12} spacing={1}>
                <Grid2 size={"grow"}>
                    <Skeleton width={200} height={skeletonHeight} />
                </Grid2>

                <Grid2>
                    <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                </Grid2>
                <Grid2>
                    <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                </Grid2>
                <Grid2>
                    <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                </Grid2>
            </Grid2>

            <Grid2 size={12}>
                <Skeleton height={240} />
            </Grid2>

            <Grid2 container size={12}>
                <Grid2>
                    <Skeleton width={82} height={skeletonHeight} />
                </Grid2>
                <Grid2>
                    <Skeleton width={96} height={skeletonHeight} />
                </Grid2>
                <Grid2>
                    <Skeleton width={69} height={skeletonHeight} />
                </Grid2>
            </Grid2>
        </Grid2>
    );
});

export default ChatDetails;
